import { gql } from '@apollo/client';
import slugify from 'slugify';
import getGraphQlClient from '~graphql/getGraphQlClient';
import { getAxiosClient } from '~src/api';
import { checkFeaturePreference, isFeatureEnabled } from '~src/features';

const REFERENCE_PAGE_SIZE = 1000;

const getProjectTypePreviewUrl = (project, isGlobal) => {
  const thumbnailUrl = project?.thumbnail?.url?.trim();
  if (thumbnailUrl) return thumbnailUrl;

  return isGlobal
    ? `./assets/blocks/project-types/progetto-${slugify(
        project.name.toLowerCase()
      )}.png`
    : './assets/blocks/placeholders/project-type.png';
};

const fetchGiottoProjectTypeReferences = async () => {
  const httpClient = await getAxiosClient();
  const [
    { data: defaultResponse },
    { data: customResponse },
  ] = await Promise.all([
    httpClient.get(
      `${process.env.PARSE_API_URL}/editor_project_types_default`,
      {
        params: {
          filterEqual_isGlobal: true,
          include: 'default_formats',
          sort_ascending: 'order',
        },
      }
    ),
    httpClient.get(`${process.env.PARSE_API_URL}/editor_project_types`),
  ]);
  const projects = [
    ...(defaultResponse?.data || []).map((project) => ({
      className: 'editor_project_types_default',
      isGlobal: true,
      project,
    })),
    ...(customResponse?.data || []).map((project) => ({
      className: 'editor_project_types',
      isGlobal: false,
      project,
    })),
  ];
  const visibleProjects = await Promise.all(
    projects.map(async ({ className, isGlobal, project }) => {
      const id = project?.objectId?.trim();
      const name = project?.name?.trim();
      if (!id || !name) return null;

      const preference = await checkFeaturePreference(
        `project.${slugify(name.toLowerCase())}`
      );
      const enabled = preference.user && preference.org;
      if (!enabled && preference.style === 'hidden') return null;

      return {
        className,
        id,
        isGlobal,
        name,
        previewUrl: getProjectTypePreviewUrl(project, isGlobal),
      };
    })
  );

  return visibleProjects.filter(Boolean);
};

const PROJECT_TYPE_REFERENCES = gql`
  query GiottoProjectTypePersonalizationReferences(
    $first: Int!
    $defaultReferencesAfter: String
    $customReferencesAfter: String
    $customTemplatePreviewsAfter: String
  ) {
    defaultReferences: findEditorProjectTypesDefault(
      first: $first
      after: $defaultReferencesAfter
      order: order_ASC
    ) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          objectId
          name
          thumbnail {
            url
          }
        }
      }
    }
    customReferences: findEditorProjectTypes(
      first: $first
      after: $customReferencesAfter
      order: order_ASC
    ) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          objectId
          name
          org_pointer {
            objectId
          }
          thumbnail {
            url
          }
        }
      }
    }
    customTemplatePreviews: findEditorTemplates(
      first: $first
      after: $customTemplatePreviewsAfter
    ) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          editor_project_types_default_pointer {
            objectId
          }
          editor_project_types_pointer {
            objectId
          }
          editor_templates_pages(first: 1, order: order_ASC) {
            edges {
              node {
                preview {
                  url
                }
              }
            }
          }
        }
      }
    }
  }
`;

const EVENT_REFERENCES = gql`
  query GiottoEventPersonalizationReferences(
    $first: Int!
    $defaultReferencesAfter: String
    $customReferencesAfter: String
    $defaultTemplatesAfter: String
    $customTemplatesAfter: String
  ) {
    defaultReferences: findEditorTemplatesCategoriesDefault(
      first: $first
      after: $defaultReferencesAfter
      order: order_ASC
    ) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          objectId
          name
          editor_project_types_default_pointer {
            objectId
            name
          }
        }
      }
    }
    customReferences: findEditorTemplatesCategories(
      first: $first
      after: $customReferencesAfter
      order: order_ASC
    ) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          objectId
          name
          editor_project_types_default_pointer {
            objectId
            name
          }
          editor_project_types_pointer {
            objectId
            name
          }
        }
      }
    }
    defaultTemplates: findEditorTemplatesDefault(
      first: $first
      after: $defaultTemplatesAfter
    ) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          editor_templates_categories_default_pointer {
            objectId
          }
          editor_templates_default_pages(first: 1, order: order_ASC) {
            edges {
              node {
                preview {
                  url
                }
              }
            }
          }
        }
      }
    }
    customTemplates: findEditorTemplates(
      first: $first
      after: $customTemplatesAfter
    ) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          editor_templates_categories_default_pointer {
            objectId
          }
          editor_templates_categories_pointer {
            objectId
          }
          editor_templates_pages(first: 1, order: order_ASC) {
            edges {
              node {
                preview {
                  url
                }
              }
            }
          }
        }
      }
    }
  }
`;

const TEMPLATE_REFERENCES = gql`
  query GiottoTemplatePersonalizationReferences(
    $first: Int!
    $defaultReferencesAfter: String
    $customReferencesAfter: String
  ) {
    defaultReferences: findEditorTemplatesDefault(
      first: $first
      after: $defaultReferencesAfter
    ) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          objectId
          name
          editor_project_types_default_pointer {
            name
          }
          editor_templates_categories_default_pointer {
            name
          }
          editor_templates_default_pages(first: 1, order: order_ASC) {
            edges {
              node {
                preview {
                  url
                }
              }
            }
          }
        }
      }
    }
    customReferences: findEditorTemplates(
      first: $first
      after: $customReferencesAfter
    ) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          objectId
          name
          editor_project_types_default_pointer {
            name
          }
          editor_project_types_pointer {
            name
          }
          editor_templates_categories_default_pointer {
            name
          }
          editor_templates_categories_pointer {
            name
          }
          editor_templates_pages(first: 1, order: order_ASC) {
            edges {
              node {
                preview {
                  url
                }
              }
            }
          }
        }
      }
    }
  }
`;

const REFERENCE_CONFIG = {
  project_type: {
    query: PROJECT_TYPE_REFERENCES,
    defaultClassName: 'editor_project_types_default',
    customClassName: 'editor_project_types',
    connections: [
      'defaultReferences',
      'customReferences',
      'customTemplatePreviews',
    ],
  },
  event: {
    query: EVENT_REFERENCES,
    defaultClassName: 'editor_templates_categories_default',
    customClassName: 'editor_templates_categories',
    connections: [
      'defaultReferences',
      'customReferences',
      'defaultTemplates',
      'customTemplates',
    ],
  },
  template: {
    query: TEMPLATE_REFERENCES,
    defaultClassName: 'editor_templates_default',
    customClassName: 'editor_templates',
    connections: ['defaultReferences', 'customReferences'],
  },
};

// Le connection vengono scorse fino in fondo: fermarsi alla prima pagina
// nasconderebbe silenziosamente modelli e categorie oltre REFERENCE_PAGE_SIZE,
// che l'utente non potrebbe più selezionare.
const fetchReferenceConnections = async (config) => {
  const graphQlClient = await getGraphQlClient();
  const fields = config.connections;
  const connections = {};
  const cursors = {};
  const pending = new Set(fields);

  while (pending.size > 0) {
    const variables = fields.reduce(
      (accumulator, field) => ({
        ...accumulator,
        [`${field}After`]: cursors[field] || null,
      }),
      { first: REFERENCE_PAGE_SIZE }
    );
    // eslint-disable-next-line no-await-in-loop
    const { data } = await graphQlClient.query({
      fetchPolicy: 'network-only',
      query: config.query,
      variables,
    });

    fields.forEach((field) => {
      if (!pending.has(field)) return;

      const connection = data?.[field];
      const edges = connection?.edges || [];
      connections[field] = {
        ...(connections[field] || connection || {}),
        edges: [...(connections[field]?.edges || []), ...edges],
      };

      const { hasNextPage, endCursor } = connection?.pageInfo || {};
      cursors[field] = endCursor || null;
      if (!hasNextPage || !endCursor || edges.length === 0) {
        pending.delete(field);
      }
    });
  }

  return connections;
};

const connectionReferences = (connection, className, isGlobal) =>
  (connection?.edges || []).flatMap((edge) => {
    const id = edge?.node?.objectId?.trim();
    if (!id) return [];

    return [
      {
        className,
        id,
        isGlobal,
        name: edge.node.name?.trim() || 'Elemento senza nome',
      },
    ];
  });

const getFirstPreviewUrl = (connection) =>
  connection?.edges
    ?.map((edge) => edge?.node?.preview?.url?.trim())
    .find(Boolean);

const collectLinkedPreviews = (
  previews,
  { categoryFields, classNames, connection, pagesField }
) => {
  (connection?.edges || []).forEach((edge) => {
    const node = edge?.node;
    const previewUrl = getFirstPreviewUrl(node?.[pagesField]);
    if (!previewUrl) return;

    categoryFields.forEach((field, index) => {
      const categoryId = node?.[field]?.objectId?.trim();
      const key = categoryId ? `${classNames[index]}:${categoryId}` : null;
      if (key && !previews.has(key)) previews.set(key, previewUrl);
    });
  });
};

const buildProjectTypePreviews = (data) => {
  const previews = new Map();
  collectLinkedPreviews(previews, {
    categoryFields: [
      'editor_project_types_default_pointer',
      'editor_project_types_pointer',
    ],
    classNames: ['editor_project_types_default', 'editor_project_types'],
    connection: data?.customTemplatePreviews,
    pagesField: 'editor_templates_pages',
  });
  return previews;
};

const buildEventPreviews = (data) => {
  const previews = new Map();
  collectLinkedPreviews(previews, {
    categoryFields: ['editor_templates_categories_default_pointer'],
    classNames: ['editor_templates_categories_default'],
    connection: data?.defaultTemplates,
    pagesField: 'editor_templates_default_pages',
  });
  collectLinkedPreviews(previews, {
    categoryFields: [
      'editor_templates_categories_default_pointer',
      'editor_templates_categories_pointer',
    ],
    classNames: [
      'editor_templates_categories_default',
      'editor_templates_categories',
    ],
    connection: data?.customTemplates,
    pagesField: 'editor_templates_pages',
  });

  return previews;
};

const projectTypeConnectionReferences = (
  connection,
  className,
  isGlobal,
  previews
) =>
  connectionReferences(connection, className, isGlobal).map((reference) => {
    const node = connection.edges.find(
      (edge) => edge?.node?.objectId?.trim() === reference.id
    )?.node;
    const defaultPreviewUrl = isGlobal
      ? `./assets/blocks/project-types/progetto-${slugify(
          reference.name.toLowerCase()
        )}.png`
      : null;
    const previewUrl =
      node?.thumbnail?.url?.trim() ||
      defaultPreviewUrl ||
      previews.get(`${className}:${reference.id}`);
    return {
      ...reference,
      ...(previewUrl ? { previewUrl } : {}),
    };
  });

const eventConnectionReferences = (connection, className, isGlobal, previews) =>
  connectionReferences(connection, className, isGlobal).map((reference) => {
    const node = connection.edges.find(
      (edge) => edge?.node?.objectId?.trim() === reference.id
    )?.node;
    const contextName =
      node?.editor_project_types_default_pointer?.name?.trim() ||
      node?.editor_project_types_pointer?.name?.trim();
    const previewUrl = previews.get(`${className}:${reference.id}`);

    return {
      ...reference,
      ...(contextName ? { contextName } : {}),
      ...(contextName ? { projectName: contextName } : {}),
      ...(previewUrl ? { previewUrl } : {}),
    };
  });

const templateConnectionReferences = (connection, className, isGlobal) =>
  connectionReferences(connection, className, isGlobal).map((reference) => {
    const node = connection.edges.find(
      (edge) => edge?.node?.objectId?.trim() === reference.id
    )?.node;
    const projectName =
      node?.editor_project_types_default_pointer?.name?.trim() ||
      node?.editor_project_types_pointer?.name?.trim();
    const categoryName =
      node?.editor_templates_categories_default_pointer?.name?.trim() ||
      node?.editor_templates_categories_pointer?.name?.trim();
    const contextName = [projectName, categoryName].filter(Boolean).join(' · ');
    const previewUrl = getFirstPreviewUrl(
      node?.[
        isGlobal ? 'editor_templates_default_pages' : 'editor_templates_pages'
      ]
    );

    return {
      ...reference,
      ...(contextName ? { contextName } : {}),
      ...(projectName ? { projectName } : {}),
      ...(previewUrl ? { previewUrl } : {}),
    };
  });

const filterAccessibleProjectReferences = async (type, references) => {
  const getProjectName = (reference) =>
    type === 'project_type'
      ? reference.name
      : reference.projectName ||
        (type === 'event' ? reference.contextName : null);
  const projectNames = [
    ...new Set(references.map(getProjectName).filter(Boolean)),
  ];
  const accessEntries = await Promise.all(
    projectNames.map(async (projectName) => {
      try {
        const enabled = await isFeatureEnabled(
          `project.${slugify(projectName.toLowerCase())}`
        );
        return [projectName, enabled];
      } catch (error) {
        return [projectName, false];
      }
    })
  );
  const accessByProjectName = new Map(accessEntries);

  return references.filter((reference) => {
    const projectName = getProjectName(reference);
    return projectName ? accessByProjectName.get(projectName) === true : false;
  });
};

export const fetchGiottoPersonalizationReferences = async (type) => {
  const config = REFERENCE_CONFIG[type];
  if (!config) return [];
  if (type === 'project_type') return fetchGiottoProjectTypeReferences();

  const data = await fetchReferenceConnections(config);
  const customReferences = data.customReferences;
  const projectTypePreviews =
    type === 'project_type' ? buildProjectTypePreviews(data) : null;
  const eventPreviews = type === 'event' ? buildEventPreviews(data) : null;
  const references = [
    ...(type === 'project_type'
      ? projectTypeConnectionReferences(
          data.defaultReferences,
          config.defaultClassName,
          true,
          projectTypePreviews
        )
      : type === 'event'
      ? eventConnectionReferences(
          data.defaultReferences,
          config.defaultClassName,
          true,
          eventPreviews
        )
      : type === 'template'
      ? templateConnectionReferences(
          data.defaultReferences,
          config.defaultClassName,
          true
        )
      : connectionReferences(
          data.defaultReferences,
          config.defaultClassName,
          true
        )),
    ...(type === 'project_type'
      ? projectTypeConnectionReferences(
          customReferences,
          config.customClassName,
          false,
          projectTypePreviews
        )
      : type === 'event'
      ? eventConnectionReferences(
          customReferences,
          config.customClassName,
          false,
          eventPreviews
        )
      : type === 'template'
      ? templateConnectionReferences(
          customReferences,
          config.customClassName,
          false
        )
      : connectionReferences(customReferences, config.customClassName, false)),
  ];

  const accessibleReferences = await filterAccessibleProjectReferences(
    type,
    references
  );

  return accessibleReferences.sort(
    (left, right) =>
      left.name.localeCompare(right.name, 'it') ||
      (left.contextName || '').localeCompare(right.contextName || '', 'it')
  );
};
